import { ITask } from 'Core/TaskAdapter'
import {
    NotificationPropsType,
    AttributeDefinitionType,
    ConversationResponseDataType,
    ContainerType,
    TaskConfigType,
    UseCaseTypes,
} from 'Types/Tasks.types'
import { createReactElement } from 'shared/utils/react.utils'
import { GenerationTaskUi } from 'Tasks/GenerationTask/UI/GenerationTaskUI'
import { Studio } from 'Core/Studio'
import { CommonTask } from 'Tasks/CommonTask/CommonTask'
import { GenerationContainerStrategy } from 'Core/StateContainerCore/ContainerStrategies/GenerationContainerStrategy'
import { ITaskBlock } from 'shared/express-interface/blocks-core/ITaskBlock'

export class GenerationTask
    extends CommonTask<ContainerType.ConversationResponse, ConversationResponseDataType>
    implements
        ITask<TaskConfigType<ContainerType.ConversationResponse, ConversationResponseDataType>>
{
    constructor(protected studio: Studio) {
        const generationContainerStrategy = new GenerationContainerStrategy()
        super(studio, generationContainerStrategy)
        generationContainerStrategy.setContext({
            task: this,
        })
    }

    public initialize(
        config: TaskConfigType<ContainerType.ConversationResponse, ConversationResponseDataType>
    ) {
        const inputEditorBlockConfig = {
            id: 'input-editor-block-1',
            name: 'input-editor-block-1',
            componentProps: {
                onUpdateContent: (content: string) => {
                    const draftOutputContainer = this.taskState.getDraftOutputContainer()
                    if (!draftOutputContainer) {
                        console.error('Cannot update task: task state is not initialized')
                        return
                    }
                    draftOutputContainer.data.data.response.content.data.value = content

                    this.autoSaveTask({
                        onComplete: (notifications?: NotificationPropsType) =>
                            this.studio.exitTask(notifications),
                    })
                },
                getValue: () =>
                    this.taskState.getDraftOutputContainer()?.data.data.response.content.data
                        .value ?? '',
            },
        }

        const promptBlock = this.studio.blockEngine.addBlock('prompt-block', {
            id: 'prompt-block-1',
            name: 'prompt-block-1',
        })

        const blocks: ITaskBlock[] = [promptBlock]

        if (
            [
                UseCaseTypes.SFT_GENERATION_RESPONSE_GENERATION,
                UseCaseTypes.SFT_GENERATION_RESPONSE_GENERATION_SUMMARISATION_USECASE,
            ].includes(this.studio.studioConfig.taskType)
        ) {
            const inputEditorBlock = this.studio.blockEngine.addBlock(
                'input-editor-block',
                inputEditorBlockConfig
            )
            const cardBlock = this.studio.blockEngine.addBlock('card-block', {
                id: 'card-block-1',
                name: 'card-block-1',
                componentProps: { children: inputEditorBlock.getElement()?.element },
            })

            blocks.push(cardBlock)
        }

        this.studio.workbench.enqueueUiUpdate(workbenchUi => {
            this.ui = createReactElement(GenerationTaskUi, {
                blocks,
            })
            this.addCommonWorkbenchComponents(workbenchUi, config)

            // add rate/review if attributes are present
            if (this.hasEditableAttributes(config)) {
                this.attributeManager?.useAttributeGroup({
                    type: AttributeDefinitionType.QUALITY_EVALUATION,
                    groupKey: 'quality-evaluation',
                    attributes: config.assignment.task.attributes.filter(
                        attr => attr.type === AttributeDefinitionType.QUALITY_EVALUATION
                    ),
                    config: { useInCollection: true },
                })
                this.attributeManager?.useAttributeGroup({
                    type: AttributeDefinitionType.EXTRA_INFO,
                    groupKey: 'extra-info',
                    attributes: config.assignment.task.attributes.filter(
                        attr => attr.type === AttributeDefinitionType.EXTRA_INFO
                    ),
                    config: { useInCollection: true },
                })
                workbenchUi.rightSidebar.addDivider()
            }
        })
    }
}
